import { MutationStatus } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import putPasswordApiCall from '../../../../api/calls/putPasswordApiCall';
import useAuth from 'ui/hooks/useAuth';
import useProtectedMutation from 'ui/hooks/useProtectedMutation';

export interface SetNewPassword {
  setNewPassword: (newPassword: string, repeatedPassword: string) => void;
  status: MutationStatus;
}

const useSetNewPassword = (): SetNewPassword => {
  const {
    token,
    emailOrPersonalId,
    isLoggedIn,
    fromTemporaryPassword,
    login,
    logout,
  } = useAuth();
  const savePasswordMutation = useProtectedMutation(putPasswordApiCall);
  const navigate = useNavigate();

  if (!token)
    throw new Error(
      'The token has not been set after logging in using temporary password.',
    );

  useEffect(() => {
    if (savePasswordMutation.status !== 'success') return;

    if (!emailOrPersonalId || !savePasswordMutation.variables?.newPassword)
      logout();
    else login(emailOrPersonalId, savePasswordMutation.variables?.newPassword);
  }, [
    savePasswordMutation.status,
    emailOrPersonalId,
    savePasswordMutation.variables?.newPassword,
  ]);

  useEffect(() => {
    if (isLoggedIn && !fromTemporaryPassword) navigate('/');
  }, [isLoggedIn, fromTemporaryPassword]);

  const setNewPassword = (newPassword: string, repeatedPassword: string) => {
    savePasswordMutation.mutate({
      newPassword,
      repeatedPassword,
      recovery: true,
    });
  };

  return {
    setNewPassword,
    status: savePasswordMutation.status,
  };
};

export default useSetNewPassword;
